import React, { useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { AppButton, AppCard, Badge, SectionLabel } from "../app";
import { appColors, appRadius, appType } from "../../utils/appTheme";
import { asambleaPropietario } from "../../utils/asambleas/api";
import {
  fechaCortaAsamblea,
  horaAsamblea,
  msDeFechaIso,
  normalizarIdioma,
  porcentajeAsamblea,
} from "../../utils/asambleas/idioma";
import { abrirEnPestana, VisorActa, VisorGrabacion } from "./VisorAsamblea";

/**
 * Historial de asambleas del propietario: una fila por asamblea terminada o
 * suspendida (fecha, tipo y modalidad, un resumen en números de los
 * convocados y presentes) con el orden del día, el acta y la grabación. El
 * acta y la grabación se ven en la app (VisorAsamblea) y se descargan aparte;
 * en el celular el acta se abre en otra pestaña (un iframe de iOS muestra
 * solo la primera hoja). Las actas publicadas sin asamblea (cargadas a mano)
 * también van como fila, solo con el acta y su grabación si la tiene.
 *
 * Props:
 *   asambleas  las terminadas y suspendidas de asambleaPropietario "listar"
 *              ({..., convocados, acta: {id, actaNumero, quorum, grabacion} | null})
 *   actas      actas publicadas sin asamblea en la lista (de "actas")
 *   esMovil    layout apilado
 */
const TIPOS = ["ordinaria", "extraordinaria"];
const MODALIDADES = ["presencial", "remota", "mixta"];

/** Asamblea o acta suelta → la fila que se pinta, más nueva primero. */
function filasDe(asambleas, actas) {
  const filas = [
    ...(asambleas || []).map((a) => ({
      clave: `a-${a.id}`,
      ms: Number(a.inicioMs) || null,
      conHora: true,
      tipo: a.tipo,
      modalidad: a.modalidad,
      cancelada: a.estado === "cancelada",
      motivo: a.estado === "cancelada" ? a.motivoCancelacion || null : null,
      ordenDelDia: Array.isArray(a.ordenDelDia) ? a.ordenDelDia : [],
      convocados: Number.isFinite(Number(a.convocados)) && a.convocados != null ? Number(a.convocados) : null,
      acta: a.acta && a.acta.id ? a.acta : null,
    })),
    ...(actas || []).map((x) => ({
      clave: `x-${x.id}`,
      ms: msDeFechaIso(x.fecha) || Number(x.publicadaEnMs) || null,
      conHora: false,
      tipo: x.tipo,
      modalidad: null,
      cancelada: false,
      motivo: null,
      ordenDelDia: null,
      convocados: null,
      acta: { id: x.id, actaNumero: x.actaNumero, publicadaEnMs: x.publicadaEnMs, quorum: x.quorum || null, grabacion: x.grabacion || null, firmada: !!x.firmada },
    })),
  ];
  return filas.sort((p, q) => (q.ms || 0) - (p.ms || 0));
}

export default function HistorialAsambleas({ asambleas = [], actas = [], esMovil = false }) {
  const { t, i18n } = useTranslation("asambleas");
  const idioma = normalizarIdioma(i18n.language);
  const [visor, setVisor] = useState(null); // { tipo: "acta" | "grabacion", fila }
  const [abriendo, setAbriendo] = useState(null); // clave de la fila cuyo PDF se abre en otra pestaña
  const [avisos, setAvisos] = useState({}); // clave → { error, enlace }

  const filas = filasDe(asambleas, actas);
  if (!filas.length) return null;

  const fechaDe = (f) => {
    const fecha = fechaCortaAsamblea(f.ms, idioma);
    return f.conHora && fecha ? `${fecha} · ${horaAsamblea(f.ms)}` : fecha;
  };
  const tituloActa = (f) => {
    const base = f.acta && f.acta.actaNumero ? t("historial.actaNumero", { numero: f.acta.actaNumero }) : t("historial.actaSinNumero");
    // Con el acta firmada subida, es la que se ve y se descarga.
    return f.acta && f.acta.firmada ? `${base} · ${t("historial.firmada")}` : base;
  };

  const verActa = async (f) => {
    if (!esMovil) {
      setVisor({ tipo: "acta", fila: f });
      return;
    }
    // En el celular, el PDF en otra pestaña (el visor del sistema muestra todas las hojas).
    if (abriendo) return;
    setAbriendo(f.clave);
    setAvisos((prev) => ({ ...prev, [f.clave]: null }));
    try {
      const enlace = await abrirEnPestana(() => asambleaPropietario("actaPdf", { actaId: f.acta.id, descargar: false }));
      if (enlace) setAvisos((prev) => ({ ...prev, [f.clave]: { enlace } }));
    } catch (_) {
      setAvisos((prev) => ({ ...prev, [f.clave]: { error: t("historial.errorActa") } }));
    } finally {
      setAbriendo(null);
    }
  };

  return (
    <section style={{ display: "flex", flexDirection: "column", gap: 12 }} aria-label={t("historial.titulo")}>
      <SectionLabel>{t("historial.titulo")}</SectionLabel>
      <AppCard padding={0} style={{ overflow: "hidden" }}>
        {!esMovil && (
          <div style={{ ...S.grilla, ...S.encabezado }} aria-hidden="true">
            <span>{t("historial.columnas.fecha")}</span>
            <span>{t("historial.columnas.asamblea")}</span>
            <span>{t("historial.columnas.convocados")}</span>
            <span style={{ textAlign: "right" }}>{t("historial.columnas.acciones")}</span>
          </div>
        )}
        <ul style={S.lista}>
          {filas.map((f, i) => (
            <Fila
              key={f.clave}
              f={f}
              primera={i === 0}
              esMovil={esMovil}
              fecha={fechaDe(f)}
              tituloActa={tituloActa(f)}
              idioma={idioma}
              abriendo={abriendo === f.clave}
              aviso={avisos[f.clave]}
              onVerActa={() => verActa(f)}
              onVerGrabacion={() => setVisor({ tipo: "grabacion", fila: f })}
            />
          ))}
        </ul>
      </AppCard>
      {visor && visor.tipo === "acta" && (
        <VisorActa
          actaId={visor.fila.acta.id}
          titulo={tituloActa(visor.fila)}
          subtitulo={fechaDe(visor.fila)}
          onCerrar={() => setVisor(null)}
        />
      )}
      {visor && visor.tipo === "grabacion" && (
        <VisorGrabacion
          actaId={visor.fila.acta.id}
          titulo={t("historial.grabacion")}
          subtitulo={`${tituloActa(visor.fila)} · ${fechaDe(visor.fila)}`}
          onCerrar={() => setVisor(null)}
        />
      )}
    </section>
  );
}

function Fila({ f, primera, esMovil, fecha, tituloActa, idioma, abriendo, aviso, onVerActa, onVerGrabacion }) {
  const { t } = useTranslation("asambleas");
  const idOrden = useId();
  const [ordenAbierto, setOrdenAbierto] = useState(false);

  const tipo = TIPOS.includes(f.tipo) ? t(`historial.tipoCorto.${f.tipo}`) : t("historial.asamblea");
  const modalidad = MODALIDADES.includes(f.modalidad) ? t(`modalidad.${f.modalidad}`) : "";
  const quorum = f.acta && f.acta.quorum ? f.acta.quorum : null;
  const resumen = [
    f.convocados != null ? t("historial.convocados", { count: f.convocados }) : null,
    quorum && quorum.unidadesPresentes != null ? t("historial.presentes", { count: Number(quorum.unidadesPresentes) || 0 }) : null,
    quorum && porcentajeAsamblea(quorum.coeficientePresente, idioma)
      ? t("historial.quorum", { pct: porcentajeAsamblea(quorum.coeficientePresente, idioma) })
      : null,
  ].filter(Boolean);
  const grabacion = f.acta && f.acta.grabacion ? f.acta.grabacion : null;
  // Sin acta: suspendida (no va a haber) o todavía en preparación.
  const textoActa = f.acta ? t("historial.verActa") : f.cancelada ? t("historial.sinActa") : t("historial.actaEnPreparacion");

  return (
    <li style={{ ...S.fila, borderTop: primera ? "none" : `1px solid ${appColors.line2}` }}>
      <div style={esMovil ? S.apilada : S.grilla}>
        <div style={S.fecha}>{fecha || "—"}</div>
        <div style={{ minWidth: 0 }}>
          <div style={appType.body}>
            {tipo}
            {modalidad ? ` · ${modalidad}` : ""}
          </div>
          {(f.acta || f.cancelada) && (
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 4, flexWrap: "wrap" }}>
              {f.acta && <span style={appType.bodySm}>{tituloActa}</span>}
              {f.cancelada && (
                <Badge tone="red" style={{ padding: "4px 9px", fontSize: 12 }}>
                  {t("estado.cancelada")}
                </Badge>
              )}
            </div>
          )}
        </div>
        <div style={{ ...appType.bodySm, minWidth: 0 }}>
          {f.motivo
            ? t("motivoCancelacion", { motivo: f.motivo })
            : resumen.length
              ? resumen.map((r, i) => (
                  // Cada dato entero en su línea ("quórum 64,5 %" no se parte).
                  <React.Fragment key={i}>
                    {i > 0 && " · "}
                    <span style={{ whiteSpace: "nowrap" }}>{r}</span>
                  </React.Fragment>
                ))
              : t("historial.sinDatos")}
        </div>
        <div role="group" aria-label={t("historial.accionesDe", { fecha })} style={esMovil ? S.accionesMovil : S.acciones}>
          {Array.isArray(f.ordenDelDia) && (
            <AppButton
              variant="outline"
              onClick={() => setOrdenAbierto((v) => !v)}
              aria-expanded={ordenAbierto}
              aria-controls={idOrden}
              style={S.boton}
            >
              {t("historial.ordenDelDia")}
            </AppButton>
          )}
          <AppButton variant="outline" onClick={onVerActa} disabled={!f.acta || abriendo} aria-busy={abriendo} style={{ ...S.boton, ...(f.acta ? S.botonActivo : null) }}>
            {textoActa}
          </AppButton>
          {(f.acta || !f.cancelada) && (
            <AppButton variant="outline" onClick={onVerGrabacion} disabled={!grabacion} style={{ ...S.boton, ...(grabacion ? S.botonActivo : null) }}>
              {grabacion ? t("historial.verGrabacion") : t("historial.sinGrabacion")}
            </AppButton>
          )}
        </div>
      </div>
      {ordenAbierto && (
        <div id={idOrden} style={S.orden}>
          <div style={appType.statLabel}>{t("ordenDelDia")}</div>
          {f.ordenDelDia.length ? (
            <ol style={{ margin: 0, paddingLeft: 18, ...appType.body }}>
              {f.ordenDelDia.map((p, i) => (
                <li key={p && p.id ? p.id : i} style={{ marginBottom: 4 }}>
                  {p && typeof p === "object" ? p.titulo : p}
                </li>
              ))}
            </ol>
          ) : (
            <div style={appType.bodySm}>{t("historial.sinPuntos")}</div>
          )}
        </div>
      )}
      {aviso && aviso.error && (
        <div role="alert" style={{ ...appType.body, color: appColors.red, marginTop: 8 }}>
          {aviso.error}
        </div>
      )}
      {aviso && aviso.enlace && (
        <a href={aviso.enlace} target="_blank" rel="noopener noreferrer" style={{ ...appType.body, color: appColors.blue, display: "inline-block", marginTop: 8 }}>
          {t("historial.abrirPestana")}
        </a>
      )}
    </li>
  );
}

const S = {
  lista: { listStyle: "none", margin: 0, padding: 0 },
  // Desktop: una línea por asamblea, columnas fijas para que se lea como tabla.
  grilla: {
    display: "grid",
    // La columna de acciones tiene ancho fijo: si dependiera de los botones de cada
    // fila (cada fila es su propia grilla), las columnas no se alinearían.
    gridTemplateColumns: "150px minmax(180px, 1fr) minmax(200px, 1fr) 400px",
    alignItems: "center",
    gap: 16,
  },
  encabezado: { ...appType.statLabel, padding: "12px 18px", borderBottom: `1px solid ${appColors.line}` },
  fila: { padding: "14px 18px" },
  apilada: { display: "flex", flexDirection: "column", gap: 6 },
  // La fecha va sin tabular-nums: en FT Habit esa feature abre espacios en "," y ":".
  fecha: { ...appType.body, color: appColors.ink, fontVariantNumeric: "normal" },
  acciones: { display: "flex", gap: 6, justifyContent: "flex-end", flexWrap: "wrap" },
  accionesMovil: { display: "flex", gap: 6, flexWrap: "wrap", marginTop: 4 },
  boton: { padding: "8px 12px", fontSize: 13, borderRadius: appRadius.sm, color: appColors.ink2 },
  botonActivo: { color: appColors.blue },
  orden: { marginTop: 12, padding: "12px 14px", background: appColors.page, borderRadius: appRadius.sm, display: "flex", flexDirection: "column", gap: 8 },
};
